import { Editor, NodeViewWrapper } from '@tiptap/react';
import React, { useContext, useEffect, useMemo } from 'react';
import { Tooltip } from '@fluentui/react-components';
import DocumentContext from '../../../contexts/DocumentContext.context';
import isDirectNodeSelection from '../../utils/isDirectNodeSelection';
import { isRegistrationSettled } from '../../../helpers/isRegistrationSettled.helper';
import { Link } from 'react-router-dom';
import { THeading, TRefData, TReference } from '../../../types';
import { getVersionId } from '@modules/items';
import { useAddRef } from '../../../api';
import useScrollContent from '@modules/documents/hooks/useScrollContent.hook';
import { useNodeRegistration } from '@modules/documents/hooks/useNodeRegistration.hook';
import getItemPathByItemType from '@modules/documents/helpers/getItemPathByItemType.helper';
import flattenTreeHeadings from '@modules/documents/helpers/flattenTreeHeadings.helper';
import { TMap } from '@shared/types';
import { getCaptionPrefix } from '@shared/backendFrontend/captionHelpersFe';
import getReferenceItemLabel from '@modules/documents/helpers/getReferenceItemLabel.helper';
import { SignInContext } from '../../../../users';
import { ControlledTooltip } from '@shared/components';
import { TERMINOLOGY } from '@shared/i18n';

type TAttrs = TReference & { loadedId?: string; originalText?: string }; // additional attributes need to be deleted when calling addRef()

type TComponentProps = {
  node: {
    attrs: any;
    nodeSize: number;
  };
  editor: Editor;
  updateAttributes: (attributes: any) => void;
  getPos: () => number | undefined;
  /** TipTap's own remove: it resolves the position and deletes the node's range. */
  deleteNode: () => void;
  selected: boolean;
};

const Component = React.memo(({ node, editor, updateAttributes, getPos, deleteNode, selected }: TComponentProps) => {
  const attrs: TAttrs = node.attrs;
  const { doc, isEdit, version, treeHeadings, captionsMap, resolveCaptionLabel } = useContext(DocumentContext);
  const {
    organization: { mock },
  } = useContext(SignInContext);
  const scrollContent = useScrollContent();

  const addRef = useAddRef();

  useNodeRegistration({
    id: attrs.id,
    loadedId: attrs.loadedId,
    isEdit,
    // SELF references are deliberately absent from refsData, so it cannot answer for them.
    registration: attrs.versionMode === 'SELF' ? 'unknown' : attrs.id in doc.refsData ? 'registered' : 'missing',
    register: async () => {
      const ref: TReference = {
        id: attrs.id,
        itemId: attrs.itemId,
        itemType: attrs.itemType,
        versionMode: attrs.versionMode,
        ...(attrs.versionId != null ? { versionId: attrs.versionId } : {}),
        ...(attrs.headingId != null ? { headingId: attrs.headingId } : {}),
        ...(attrs.bookmark != null ? { bookmark: attrs.bookmark } : {}),
      };
      await addRef.mutateAsync({ docId: doc._id, ref });
    },
    editor,
    getPos,
    removeNode: deleteNode,
  });

  const content = useMemo(() => {
    if (!isRegistrationSettled(attrs)) return <span className="ref-loading">Loading...</span>;

    let refData: TRefData | undefined;
    if (attrs.versionMode === 'SELF') {
      // the same logic is used in htmlToPdf.ts
      const versionHeadingsMap: TMap<string, THeading[]> = {};
      if (doc.draft) versionHeadingsMap['draft'] = isEdit ? flattenTreeHeadings(treeHeadings) : doc.draft.headings;

      for (const v of doc.versions) versionHeadingsMap[v.id] = v.headings;

      refData = { itemName: doc.name, itemActive: true };

      let foundVersion = false;
      for (const versionId in versionHeadingsMap) {
        if (versionId === (version ? getVersionId(version) : 'draft')) {
          foundVersion = true;
          const heading = versionHeadingsMap[versionId].find((h) => h.id === attrs.headingId);
          if (heading) {
            refData.headingData = { text: heading.text, numbering: heading.numbering };
            break;
          }
        } else if (foundVersion) {
          const heading = versionHeadingsMap[versionId].find((h) => h.id === attrs.headingId);
          if (heading) {
            refData.headingData = {
              text: heading.text,
              numbering: heading.numbering,
              fallbackVersionId: versionId,
            };
            break;
          }
        }
      }
    } else refData = doc.refsData[attrs.id];

    if (!refData) return <span className="ref-error">Cannot load {TERMINOLOGY.reference.lower}</span>;

    let itemText = '';
    let headingText = '';
    const isH5Ref = refData.headingData?.numbering.split('.').length === 6 && mock?.isH5RefEnabled;
    if (isH5Ref) {
      headingText = refData.headingData?.text || 'Deleted ref ID';
    } else {
      itemText = getReferenceItemLabel(refData.itemName);
      if (attrs.versionMode === 'SPECIFIC' && refData.versionId)
        itemText += !isH5Ref ? ` (${refData.versionId === 'draft' ? '' : 'v'}${refData.versionId})` : '';
      if (attrs.headingId) {
        itemText += ' › ';
        if (refData.headingData) headingText += `${refData.headingData.numbering} ${refData.headingData.text}`;
        else headingText += 'Deleted section';
      }
    }

    if (!refData.itemActive)
      return (
        <Tooltip content="Item was archived." relationship="description" withArrow>
          <span className="ref-error">
            {itemText}
            {headingText}
          </span>
        </Tooltip>
      );

    const bookmarkId = attrs.bookmark?.id;
    if (bookmarkId) {
      if (!captionsMap) return <span>Loading...</span>;
      const captionEntry = captionsMap[bookmarkId];
      const originalText = attrs.originalText || `Unknown ${TERMINOLOGY.reference.lower}`;
      if (!captionEntry)
        return (
          <ControlledTooltip
            content={`${TERMINOLOGY.reference.upper} not found. The ${attrs.bookmark?.kind ? 'caption' : 'section'} may have been removed or was not recognized during import.`}
            enable={!!originalText}
            relationship="description"
            withArrow
          >
            <span className="ref-error">{originalText}</span>
          </ControlledTooltip>
        );

      return (
        <span className="ref-success" onClick={() => scrollContent({ value: bookmarkId })}>
          {getCaptionPrefix(captionEntry, resolveCaptionLabel)}
        </span>
      );
    }

    let href = `/${getItemPathByItemType(attrs.itemType)}/${node.attrs.itemId}?view=`;
    if (attrs.versionMode === 'SPECIFIC' && refData.versionId) href += refData.versionId;
    else href += 'latest';

    if (!attrs.headingId)
      return (
        <Link to={href} target="_blank" className="ref-success unstyled">
          {itemText}
        </Link>
      );
    const headingId = attrs.headingId;

    let headingErrorMessage = '';
    const versionText =
      attrs.versionMode === 'SELF'
        ? 'this version'
        : attrs.versionMode === 'AUTO'
          ? 'latest version/draft'
          : `version ${refData.versionId}`;
    if (!refData.headingData) headingErrorMessage = `Section was deleted in ${versionText}.`;
    else if (refData.headingData.fallbackVersionId)
      headingErrorMessage = `Section was deleted in ${versionText}. The section can be found in the older version ${refData.headingData.fallbackVersionId}.`;

    if (headingErrorMessage)
      return (
        <Tooltip content={headingErrorMessage} relationship="description" withArrow>
          <span>
            {attrs.versionMode !== 'SELF' && (
              <Link to={href} target="_blank" className="ref-success unstyled">
                {itemText}
              </Link>
            )}
            <span className="ref-error">{headingText}</span>
          </span>
        </Tooltip>
      );

    if (attrs.versionMode === 'SELF')
      return (
        <span className="ref-success" onClick={() => scrollContent({ value: headingId })}>
          {headingText}
        </span>
      );

    href += `&elem=${headingId}`;

    return (
      <Link to={href} target="_blank" className="ref-success unstyled">
        {itemText}
        {headingText}
      </Link>
    );
  }, [
    doc.refsData,
    version,
    treeHeadings,
    captionsMap,
    isEdit,
    attrs.id,
    attrs.loadedId,
    attrs.bookmark,
    attrs.originalText,
  ]);

  return (
    <NodeViewWrapper
      as="span"
      id={attrs.id}
      data-drag-handle
      className={`reference ${isDirectNodeSelection(editor, selected, isEdit) ? 'selected' : ''}`}
    >
      {content}
    </NodeViewWrapper>
  );
});

export default Component;
